// Voice's calendar write — issue #1686.
//
// ── Why there is no new Edge Function ────────────────────────────────────────
//
// The obvious shape for this was a new Pro-gated Edge Function wrapping
// executeCreateEventTool, because that is where kin-chat calls it from. It is not
// needed, and standing one up would have been a new live endpoint for nothing:
//
//   - `executeCreateEventTool` takes an INJECTED client (`CreateEventClient` is
//     `{ from }`) and touches no Deno global. The browser's supabase client
//     satisfies it exactly. src/lib/__tests__/createEventWrite.test.ts has been
//     driving it with a plain object since #1670.
//   - `src/` already imports Edge modules directly — entitlementActive.ts and
//     upcomingTaskReminders.ts both do, for this same "one rule, two runtimes"
//     reason.
//   - RLS on `events` permits the owner's insert, and the app already inserts
//     there from the browser (partyCalendarMirror.ts, TodayTab's manual add).
//     Under her own JWT the insert is narrower than a service-role one, not
//     wider: Postgres enforces `user_id = auth.uid()` rather than Kin promising
//     to.
//
// So voice runs the SAME write chat runs — her timezone, the kid-name match, the
// past-date guard, the duplicate pre-check, the house-rule-16 verified read, the
// durable birthday fact. Not a copy of it. Three hand-synced copies of one rule
// is this codebase's most common defect shape (#361, #470, #515, and #1685 two
// days ago), and a second event-insert in the browser would have been the third.
//
// ── ⚠️ What voice's event write does NOT have, stated plainly ────────────────
//
// `events` has NO `propose_id` column and NO budget trigger, so this write is
// outside the two mechanisms that back voice's other three writes
// (20260816120000, 20260829090000). That is a real gap and it is deliberate for
// now, for reasons worth writing down rather than discovering later:
//
//   IDEMPOTENCY is covered in practice by two things that already exist. The
//   gate resets BEFORE this runs (decideVoiceWrite returns nextState =
//   INITIAL), so a duplicated create_event tool call for one confirmation is
//   refused as unconfirmed and writes nothing. And a genuine retry after a
//   failed-looking-but-committed insert is caught by `findDuplicate`, which
//   matches user_id + event_date + title + status — the case a unique index
//   would have caught, caught by the pre-check instead.
//
//   THE WRITE CAP genuinely is not enforced here. What bounds it instead is the
//   two-step: every event costs a propose, a real transcribed user turn, and a
//   yes. VOICE_WRITE_CAP is 12 per 10 minutes — one every 50 seconds — and a mom
//   confirming events out loud one at a time does not reach that by accident.
//
// Filed as #1694 rather than bolted on here, because closing it properly
// means a migration on `events`: a table written by chat, by Google and Apple
// import, by photo import, by the party mirror and by the Today tab. That is not
// a change to make inside a PR about voice.

import { supabase } from "@/integrations/supabase/client";
import {
  executeCreateEventTool,
  type CreateEventStatus,
} from "../../supabase/functions/kin-chat/createEventWrite";
// #1694 — the spoken words for the two outcomes the database can produce. They
// live here, in voice's own module, rather than in the Edge module that owns the
// insert: `resultText` from there is written for a text model, and these two are
// about to be synthesized to audio.
import { VOICE_WRITE_ALREADY_SAVED_RESULT, VOICE_WRITE_RATE_LIMITED_RESULT } from "./voiceWriteBudget";

export interface VoiceEventArgs {
  title?: unknown;
  event_date?: unknown;
  time_mode?: unknown;
  start_time?: unknown;
  location?: unknown;
  child?: unknown;
}

export interface VoiceCreateEventResult {
  /** False only when nothing is on her calendar as a result of this turn. */
  ok: boolean;
  /** Handed to the model verbatim. Already phrased as an instruction to speak. */
  result: string;
  status: CreateEventStatus;
  /** Whether a NEW row landed — the only case that earns a toast. */
  wrote: boolean;
  /** Title and day, for the toast and the chat receipt line. Null unless written. */
  event: { title: string; eventDate: string; isAllDay: boolean; startTime: string | null } | null;
}

export const VOICE_EVENT_NOT_SIGNED_IN =
  "You're not signed in right now, so nothing could be saved. Tell her you couldn't add it and to try again in a moment.";

export const VOICE_EVENT_THREW =
  "Something went wrong adding that to her calendar and NOTHING was saved. " +
  "Tell her plainly it did not save and offer to try again — do not say it's on her calendar.";

/**
 * Put one event on her calendar from a voice call.
 *
 * ⚠️ Never throws. A tool handler that throws leaves the realtime model waiting
 * on a function_call_output that never arrives and Kin goes silent mid-call —
 * the same reason voiceWriteBudget.ts states in its header. Every failure comes
 * back as words the model can say.
 *
 * ⚠️ `ok` is false for `not_saved` and TRUE for `already_there`, and that split
 * is the point. "It was already on there" is a success from her side — the thing
 * she wanted to be true is true — and returning it as a failure makes Kin
 * apologise for having already done it. What must never be true is `ok` with
 * nothing on her calendar.
 */
export async function createVoiceEvent(
  args: VoiceEventArgs,
  userId: string | null | undefined,
  // #1694 — the id the capture gate minted when Kin proposed this event. Passing
  // it is what makes the write idempotent: the same yes can only ever produce one
  // row, enforced by a partial unique index rather than by the client being
  // careful. Defaulted so a caller that forgets it still writes, unindexed,
  // exactly as #1686 shipped.
  proposeId: string | null = null,
): Promise<VoiceCreateEventResult> {
  if (!userId) {
    return { ok: false, result: VOICE_EVENT_NOT_SIGNED_IN, status: "not_saved", wrote: false, event: null };
  }

  try {
    const outcome = await executeCreateEventTool(args, supabase as any, userId, "kin_voice", proposeId);
    const wrote = outcome.status === "written" && !!outcome.event;
    const raw = outcome.event as Record<string, unknown> | null;
    return {
      // ⚠️ Derived from `status`, never from `resultText`. See CreateEventStatus.
      //
      // #1694 — `duplicate` is a SUCCESS: the row this proposal wanted exists, so
      // the thing she asked for is true. `rate_limited` is not: nothing was
      // written, and it must not read as one.
      ok: outcome.status !== "not_saved" && outcome.status !== "rate_limited",
      // #1694 — these two get voice's own words. VOICE_WRITE_RATE_LIMITED_RESULT
      // ends with "Don't retry", which is the whole point of telling the cap apart
      // from a failure; VOICE_WRITE_ALREADY_SAVED_RESULT tells the model not to
      // mention the duplicate at all, so one yes never produces two spoken
      // confirmations. Both are already used by voice's other three writes, so all
      // four refusals now sound the same out loud.
      result:
        outcome.status === "rate_limited"
          ? VOICE_WRITE_RATE_LIMITED_RESULT
          : outcome.status === "duplicate"
            ? VOICE_WRITE_ALREADY_SAVED_RESULT
            : outcome.resultText,
      status: outcome.status,
      wrote,
      event: wrote
        ? {
            title: String(raw?.title ?? ""),
            eventDate: String(raw?.eventDate ?? ""),
            isAllDay: !!raw?.isAllDay,
            startTime: (raw?.startTime as string | null) ?? null,
          }
        : null,
    };
  } catch (e) {
    console.error("[voice create_event] threw:", e);
    return { ok: false, result: VOICE_EVENT_THREW, status: "not_saved", wrote: false, event: null };
  }
}
